import { useCallback, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import type { ImageRef } from 'shared/lib/widgetCode/normalizeWidgetCode';
import { moveImagesToAssets, MoveImagesResult } from '~/lib/moveImagesToAssets';

const IMPORT_IMAGE = gql`
  mutation ImportImageToAssets($source: String!, $folder: String!) {
    importImageToAssets(source: $source, folder: $folder) {
      key
      url
    }
  }
`;

/** Moves a widget's embedded and outside images into Assets, one at a time. */
export default function useMoveImages(): {
  move: (code: { html: string; css: string }, images: ImageRef[], folder: string) => Promise<MoveImagesResult>;
  busy: boolean;
} {
  const [importImage] = useMutation(IMPORT_IMAGE);
  const [busy, setBusy] = useState(false);

  const move = useCallback(async (code: { html: string; css: string }, images: ImageRef[], folder: string) => {
    setBusy(true);
    try {
      return await moveImagesToAssets(code, images, folder, async (source, f) => {
        const { data } = await importImage({ variables: { source, folder: f } });
        return data.importImageToAssets;
      });
    } finally {
      setBusy(false);
    }
  }, [importImage]);

  return { move, busy };
}

/** A short message about images that couldn't be moved, or null. */
export const describeFailures = (failed: MoveImagesResult['failed']): string | null => {
  if (!failed.length) return null;
  const first = failed[0].message;
  return failed.length === 1 ? `Couldn’t move 1 image: ${first}` : `Couldn’t move ${failed.length} images. First problem: ${first}`;
};
